import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, message, notification, Radio, Tag, Tooltip } from "antd";
import {
  CloseCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import legalIcon from "../../../../Assets/Icons/apartment.svg";
import dateIcon from "../../../../Assets/Icons/calendar_month.svg";
import regIcon from "../../../../Assets/Icons/markdown_copy.svg";
import currencyIcon from "../../../../Assets/Icons/attach_money.svg";
import contactIcon from "../../../../Assets/Icons/account_child_invert.svg";
import mailIcon from "../../../../Assets/Icons/mail.svg";
import locIcon from "../../../../Assets/Icons/distance.svg";
import keyIcon from "../../../../Assets/Icons/password_2.svg";
import FuelIcon from "../../../../Assets/Icons/fuel-icon.svg";
import ClipboardIcon from "../../../../Assets/Icons/clipboard.svg";
import CarPartsIcon from "../../../../Assets/Icons/car_repair.svg";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import ModuleSelctorCards from "../../../../Components/Cards/ModuleSelectorCards";
import "./style.css";
import {
  getApprovalById,
  rejectApproval,
  approveApproval,
  requestRevision,
} from "../../../../API/Admin/Approval&Requests";
import { getDocumentCategories } from "../../../../API/Config";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
};

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString("en-US");
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const MODULE_ICONS_BY_CODE = {
  fuel: FuelIcon,
  maintenance: ClipboardIcon,
  parts: CarPartsIcon,
};

const PRICING_TABS = ["Monthly Pricing", "Annually Pricing"];

const cycleToPeriod = (cycle) =>
  cycle === "annual"
    ? "Annually Pricing"
    : cycle === "monthly"
      ? "Monthly Pricing"
      : null;

const buildModuleOption = (mod, isAnnual) => {
  const fallbackIcon =
    MODULE_ICONS_BY_CODE[mod.code?.toLowerCase()] || ClipboardIcon;
  const iconSrc = mod.icon ? resolveFileUrl(mod.icon) : fallbackIcon;
  const rawPrice = isAnnual
    ? mod.annual_price_per_vehicle
    : mod.monthly_price_per_vehicle;
  const price = parseFloat(rawPrice);
  const isInactive = mod.status && mod.status !== "active";

  return {
    id: mod.id,
    title: mod.name,
    description: mod.description,
    price: rawPrice
      ? price
        ? `${mod.currency || "JMD"} ${price.toFixed(2)}/vehicle`
        : "Free"
      : undefined,
    icon: <img src={iconSrc} alt={mod.name} />,
    disabled: isInactive,
    tag: isInactive ? "Coming Soon" : undefined,
  };
};

const CompanyOnboarding = () => {
  const { canEdit } = usePageAccess("approvals-requests");
  const navigate = useNavigate();
  const location = useLocation();
  const passedId = location.state?.id;
  const [remarks, setRemarks] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [requestingRevision, setRequestingRevision] = useState(false);
  const [request, setRequest] = useState({
    company: "",
    legalName: "-",
    dateIncorporated: "-",
    registrationNumber: "-",
    currency: "-",
    primaryContact: "-",
    contactEmail: "-",
    location: "-",
    apiKeys: [],
    status: "-",
    avatar: null,
    vehicleLimit: 0,
  });

  const statusMap = {
    approved: "status-approved",
    rejected: "status-denied",
    denied: "status-denied",
    pending: "status-pending",
    revision_requested: "status-revision-requested",
  };
  const currentStatus = request.status;
  const formatStatus = (value) =>
    value
      ? value
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      : value;

  const [documents, setDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [assignedModules, setAssignedModules] = useState([]);
  const [pricingPeriod, setPricingPeriod] = useState(PRICING_TABS[0]);
  const [planPricingPeriod, setPlanPricingPeriod] = useState(null);
  const isAnnual = pricingPeriod === "Annually Pricing";
  const moduleOptions = assignedModules.map((mod) =>
    buildModuleOption(mod, isAnnual),
  );

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No request selected. Please open this page from the Approvals & Requests list.",
      );
      return;
    }

    const fetchApproval = async () => {
      try {
        const res = await getApprovalById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          const company = data.entity || {};
          const owner = data.submitted_by || {};

          setRequest({
            company: company.legal_name || company.brand_name || "-",
            legalName: company.legal_name || "-",
            dateIncorporated: formatDate(company.date_incorporated),
            registrationNumber: company.registration_number || "-",
            currency: company.currency || "-",
            primaryContact: owner.name || company.primary_contact_person || "-",
            contactEmail: company.contact_email || owner.email || "-",
            location: company.address || "-",
            apiKeys: Array.isArray(company.navixy_api_keys)
              ? company.navixy_api_keys
              : [],
            status: data.status || "-",
            avatar: resolveFileUrl(company.avatar),
            vehicleLimit: company.vehicle_limit || 0,
          });

          setAssignedModules(
            Array.isArray(company.assigned_modules)
              ? company.assigned_modules
              : [],
          );
          const planPeriod = cycleToPeriod(
            company.assigned_modules?.[0]?.billing_cycle,
          );
          setPlanPricingPeriod(planPeriod);
          if (planPeriod) setPricingPeriod(planPeriod);

          if (Array.isArray(company.documents)) {
            setDocuments(
              company.documents.map((doc) => ({
                key: String(doc.id),
                name: doc.file?.split("/").pop() || doc.category || "-",
                category: doc.category || "-",
                allowedFormats: doc.allowed_formats || [],
                date: formatDate(doc.uploaded_at),
                expiryDate: formatDate(doc.expiry_date),
                status: "Pending",
                fileUrl: resolveFileUrl(doc.file),
              })),
            );
          }
        } else {
          message.error(res?.data?.message || "Failed to load request.");
        }
      } catch (err) {
        message.error(
          err?.response?.data?.message || "Failed to load request.",
        );
      }
    };

    fetchApproval();
  }, [passedId]);

  useEffect(() => {
    const fetchDocumentCategories = async () => {
      const res = await getDocumentCategories("fleet-manager");
      const list = res?.data?.data;
      if (res?.status === 200 && Array.isArray(list)) {
        setDocumentCategories(
          list.map((cat) => ({
            label: cat.document_name,
            value: cat.document_name,
            allowedFormats: cat.allowed_formats,
          })),
        );
      }
    };

    fetchDocumentCategories();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleReject = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before rejecting this company.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setRejecting(true);
    try {
      const res = await rejectApproval(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.error({
          message: res?.data?.message || "Company Rejected",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to reject request.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to reject request.",
      );
    } finally {
      setRejecting(false);
    }
  };

  const handleRevision = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before requesting a revision.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setRequestingRevision(true);
    try {
      const res = await requestRevision(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        const devToken = res?.data?.data?.dev_revision_token;
        notification.success({
          message: res?.data?.message || "Revision Requested",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to request revision.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to request revision.",
      );
    } finally {
      setRequestingRevision(false);
    }
  };

  const handleApprove = async () => {
    if (!remarks.trim()) {
      notification.warning({
        message: "Audit Notes Required",
        description: "Please enter audit notes before approving this company.",
      });
      return;
    }

    if (!passedId) {
      message.error("No request selected.");
      return;
    }

    setApproving(true);
    try {
      const res = await approveApproval(passedId, remarks);

      if (Math.floor(res?.status / 100) === 2) {
        notification.success({
          message: res?.data?.message || "Company Approved",
        });
        navigate("/approvals-requests");
      } else {
        message.error(res?.data?.message || "Failed to approve request.");
      }
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to approve request.",
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="company-onboard">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="vendor-header-card">
        <div className="vendor-info-wrapper">
          {request.avatar ? (
            <img
              src={request.avatar}
              alt="Company Logo"
              className="vendor-logo"
            />
          ) : (
            <div
              className="vendor-logo vendor-logo-fallback"
              style={{ background: getAvatarColor(request.company) }}
            >
              {getInitials(request.company)}
            </div>
          )}

          <div className="vendor-details">
            <div className="company-name">{request.company}</div>
            <div className={`status-badge ${statusMap[currentStatus]}`}>
              {formatStatus(currentStatus)}
            </div>
          </div>
        </div>
      </div>
      <div className="info">
        <div className="info-card">
          <div className="info-grid">
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={legalIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Legal Name</div>
              </div>
              <div className="info-value">{request.legalName}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={dateIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Date Incorporated</div>
              </div>
              <div className="info-value">{request.dateIncorporated}</div>
            </div>
            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={regIcon} alt="Fuel Icon" />
                </div>
                <div className="info-label">Registration Number</div>
              </div>
              <div className="info-value">{request.registrationNumber}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={currencyIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Currency</div>
              </div>
              <div className="info-value">{request.currency}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={contactIcon} alt="Contact Icon" />
                </div>
                <div className="info-label">Primary Contact</div>
              </div>
              <div className="info-value">{request.primaryContact}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={mailIcon} alt="Curr Icon" />
                </div>
                <div className="info-label">Contact Email</div>
              </div>
              <div className="info-value">{request.contactEmail}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={locIcon} alt="lov Icon" />
                </div>
                <div className="info-label">Location</div>
              </div>
              <div className="info-value">{request.location}</div>
            </div>

            <div className="item">
              <div className="card-wrapper">
                <div className="icon">
                  <img src={keyIcon} alt="API Key Icon" />
                </div>
                <div className="info-label">API Key</div>
              </div>
              <div className="info-value api-keys-value">
                {request.apiKeys?.length
                  ? request.apiKeys.map((key) => (
                      <Tooltip
                        key={key}
                        title={<span style={{ wordBreak: "break-all" }}>{key}</span>}
                      >
                        <Tag className="api-key-tag">{key}</Tag>
                      </Tooltip>
                    ))
                  : "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <GenericDocumentManagerTable
          customTitle="Compliance Document"
          initialDocuments={documents}
          documentTypes={documentCategories}
          showTableOnly={true}
          hideRemoveAction={true}
        />
      </div>

      <div className="info request-modules-section">
        <div className="request-modules-header">
          <div className="info-title">Request Modules</div>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            className="custom-segmented-control"
            value={pricingPeriod}
            onChange={(e) => setPricingPeriod(e.target.value)}
            options={PRICING_TABS.map((tab) => ({
              label: tab.replace(" Pricing", ""),
              value: tab,
              disabled: !!planPricingPeriod && tab !== planPricingPeriod,
            }))}
          />
        </div>
        <ModuleSelctorCards
          isPricingModule={true}
          options={moduleOptions}
          selectedIds={moduleOptions.map((m) => m.id)}
          multiple={true}
          allowSelection={false}
          showFooter={true}
          footerLabel={isAnnual ? "Annual Rate" : "Monthly Rate"}
          showVehicleInput={true}
          vehicleCount={request.vehicleLimit}
        />
      </div>

      {canEdit &&
        ["pending", "revision_requested"].includes(
          String(currentStatus).toLowerCase(),
        ) && (
        <div className="decision-panel">
          <div className="info-title">Decision Panel</div>
          <div className="decision-card">
            <div className="audit-section">
              <label>Audit Notes</label>
              <textarea
                placeholder="Enter a description..."
                className="audit-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
            <div className="decision-actions">
              <Button
                className="reject-btn"
                icon={<CloseCircleOutlined />}
                onClick={handleReject}
                loading={rejecting}
                disabled={rejecting}
              >
                Reject Company
              </Button>

              <Button
                className="revision-btn"
                icon={<ReloadOutlined />}
                onClick={handleRevision}
                loading={requestingRevision}
                disabled={requestingRevision}
              >
                Request Revision
              </Button>

              <Button
                type="primary"
                className="approve-btn"
                icon={<CheckCircleOutlined />}
                onClick={handleApprove}
                loading={approving}
                disabled={approving}
              >
                Approve Company
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyOnboarding;
